import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import Toast from '../components/Toast';
import BrandSelector from '../components/BrandSelector';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Pages
import RequestCampaign from './brand/RequestCampaign';
import MyRequests from './brand/MyRequests';
import MyMetrics from './brand/MyMetrics';
import RequestQueue from './admin/RequestQueue';
import AllCampaignsData from './admin/AllCampaignsData';
import UserManagement from './admin/UserManagement';
import BackOffice from './admin/BackOffice';
import FeatureRequests from './admin/FeatureRequests';
import Analytics from './shared/Analytics';
import AutomationAnalytics from './shared/AutomationAnalytics';
import FlowBuilder from './shared/FlowBuilder';
import ContentLibraryView from './shared/ContentLibraryView';
import CreativeLibraryView from './shared/CreativeLibraryView';
import AdminPanel from './admin/AdminPanel';
import RequestFeature from './shared/RequestFeature';
import CompareCampaigns from './shared/CompareCampaigns';
import OneViewAnalyzer from './shared/OneViewAnalyzer';
import BookingCalendar from '../components/BookingCalendar';
import AuditLogView from './admin/AuditLogView';

const Dashboard = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isViewer = user?.role === 'viewer';
  
  // Brand selector for viewer role (can switch between brands)
  const [selectedBrand, setSelectedBrand] = useState(null);
  
  // Set initial page based on role
  const initialPage = isAdmin ? 'queue' : 'request';
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [toast, setToast] = useState(null);

  // Compare-view state — a lightweight in-memory selection payload used by the
  // dedicated /compare pages. Kept at Dashboard level so we can navigate to the
  // compare view from any sub-page and back.
  const [compareContext, setCompareContext] = useState({ type: null, ids: [] });

  const openCompareCampaigns = (ids = []) => {
    setCompareContext({ type: 'campaigns', ids });
    setCurrentPage('compare-campaigns');
  };

  const backFromCompare = () => {
    // Return the user to the source page for the current compare context
    if (compareContext.type === 'campaigns') setCurrentPage('campaigns-data');
    else setCurrentPage(initialPage);
    setCompareContext({ type: null, ids: [] });
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // Define page order for pagination (viewers see same as brand managers but read-only)
  const pages = isAdmin 
    ? ['queue', 'booking-calendar', 'campaigns-data', 'one-view-analyzer', 'analytics', 'automation', 'flow-builder', 'users', 'admin-panel', 'backoffice']
    : ['request', 'my-requests', 'campaigns-data', 'one-view-analyzer', 'analytics', 'automation', 'flow-builder', 'content-library', 'creative-library'];

  const currentPageIndex = pages.indexOf(currentPage);
  const hasPrevious = currentPageIndex > 0;
  const hasNext = currentPageIndex < pages.length - 1;

  const goToPreviousPage = () => {
    if (hasPrevious) {
      setCurrentPage(pages[currentPageIndex - 1]);
    }
  };

  const goToNextPage = () => {
    if (hasNext) {
      setCurrentPage(pages[currentPageIndex + 1]);
    }
  };

  const renderPage = () => {
    // For viewers, pass selectedBrand to filter data
    const viewerProps = isViewer ? { selectedBrand, isViewOnly: true } : {};
    
    switch (currentPage) {
      // Brand pages (viewers see these but read-only)
      case 'request':
        return <RequestCampaign showToast={showToast} {...viewerProps} />;
      case 'my-requests':
        return <MyRequests showToast={showToast} {...viewerProps} />;
      case 'my-metrics':
        return <MyMetrics showToast={showToast} {...viewerProps} />;
      
      // Admin pages
      case 'queue':
        return <RequestQueue showToast={showToast} />;
      case 'campaigns-data':
        return <AllCampaignsData showToast={showToast} onCompare={openCompareCampaigns} {...viewerProps} />;
      case 'users':
        return <UserManagement showToast={showToast} />;
      case 'backoffice':
        return <BackOffice showToast={showToast} />;
      case 'feature-requests':
        return <FeatureRequests showToast={showToast} />;
      
      // Shared pages
      case 'analytics':
        return <Analytics showToast={showToast} {...viewerProps} />;
      
      case 'automation':
        return <AutomationAnalytics showToast={showToast} />;
      
      case 'flow-builder':
        return <FlowBuilder showToast={showToast} />;
      
      case 'content-library':
        return <ContentLibraryView showToast={showToast} />;
      
      case 'creative-library':
        return <CreativeLibraryView showToast={showToast} />;
      
      case 'admin-panel':
        return <AdminPanel showToast={showToast} />;
      
      case 'request-feature':
        return <RequestFeature showToast={showToast} />;

      case 'compare-campaigns':
        return (
          <CompareCampaigns
            selectedIds={compareContext.ids}
            onBack={backFromCompare}
            showToast={showToast}
          />
        );

      case 'one-view-analyzer':
        return <OneViewAnalyzer showToast={showToast} />;

      case 'booking-calendar':
        return <BookingCalendar />;

      case 'audit-log':
        return <AuditLogView showToast={showToast} />;

      default:
        return <div className="p-8 text-white">Page not found</div>;
    }
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <Sidebar currentPage={currentPage} setCurrentPage={setCurrentPage} />
      
      <main className="flex-1 overflow-y-auto relative flex flex-col">
        <Header
          currentPage={currentPage}
          rightSlot={isViewer ? (
            <BrandSelector selectedBrand={selectedBrand} onBrandChange={setSelectedBrand} />
          ) : null}
        />
        
        <div className="flex-1">
          {renderPage()}
        </div>

        {/* Pagination Controls */}
        <div className="fixed bottom-8 right-8 flex gap-3 z-30">
          <button
            onClick={goToPreviousPage}
            disabled={!hasPrevious}
            className={`
              p-3 rounded-xl shadow-lg transition-all duration-200
              ${hasPrevious 
                ? 'bg-teal-600 hover:bg-teal-700 text-white cursor-pointer transform hover:-translate-y-0.5' 
                : 'bg-slate-700/50 text-slate-500 cursor-not-allowed'
              }
            `}
            style={hasPrevious ? { backgroundColor: '#2EB4AC' } : {}}
            onMouseEnter={(e) => hasPrevious && (e.target.style.backgroundColor = '#26A199')}
            onMouseLeave={(e) => hasPrevious && (e.target.style.backgroundColor = '#2EB4AC')}
            title="Previous page"
          >
            <ChevronLeft size={20} />
          </button>
          
          <button
            onClick={goToNextPage}
            disabled={!hasNext}
            className={`
              p-3 rounded-xl shadow-lg transition-all duration-200
              ${hasNext 
                ? 'bg-teal-600 hover:bg-teal-700 text-white cursor-pointer transform hover:-translate-y-0.5' 
                : 'bg-slate-700/50 text-slate-500 cursor-not-allowed'
              }
            `}
            style={hasNext ? { backgroundColor: '#2EB4AC' } : {}}
            onMouseEnter={(e) => hasNext && (e.target.style.backgroundColor = '#26A199')}
            onMouseLeave={(e) => hasNext && (e.target.style.backgroundColor = '#2EB4AC')}
            title="Next page"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </main>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default Dashboard;
